/**
 * Canonical driver-rejection reason keys, shared by the admin UI and the API.
 * Backend stores the reason free-form; keep this list in sync with what
 * reviewers can actually submit.
 *
 * Driver rejection is separate from document review: these reasons must never
 * be reused for document decisions.
 */
export const DRIVER_REJECTION_REASONS = [
  "duplicate_or_fraudulent",
  "profile_not_eligible",
  "no_current_need",
  "other",
] as const;

export type DriverRejectionReason = (typeof DRIVER_REJECTION_REASONS)[number];

/**
 * French source of truth for driver rejection labels. The admin translates the
 * same codes through `driver.rejectReason.*`.
 */
export const DRIVER_REJECTION_REASON_LABELS_FR: Record<DriverRejectionReason, string> = {
  duplicate_or_fraudulent: "Candidature en doublon ou frauduleuse",
  profile_not_eligible: "Profil non éligible",
  no_current_need: "Pas de besoin actuel",
  other: "Autre",
};

export const isDriverRejectionReason = (
  reason: string | null | undefined
): reason is DriverRejectionReason =>
  !!reason && (DRIVER_REJECTION_REASONS as readonly string[]).includes(reason);

/** Short French label for a driver reason code; free-form reasons are returned as-is. */
export function getDriverRejectionReasonLabelFr(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return isDriverRejectionReason(reason) ? DRIVER_REJECTION_REASON_LABELS_FR[reason] : reason;
}
